"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Typography } from "@/components/common/ui/Typography";
import { Dialog } from "@/components/common/ui/Dialog";
import { Button } from "@/components/forms/Button";
import { Input } from "@/components/forms/Input";
import { useAuthStore } from "@/lib/auth/store";
import {
  fetchFieldCatalog,
  fetchReportConfig,
  createProblemReport,
} from "@/lib/reports/api";
import type { FlaggedField } from "@/lib/reports/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  applicationId: number;
  quoteResultId?: number | null;
  vertical: string;
  insuredName?: string | null;
  // Activity Timeline pre-fill (LIB-124). Threaded into the report `context` so
  // support-api can correlate + FK-gate the problem_reported event. task_id is
  // present only for gateway-real (email) submissions — the producer's emit gate.
  taskId?: string | null;
  carrierDisplayName?: string | null;
  coverageType?: string | null;
  quoteData?: Record<string, unknown> | null;
}

interface EnabledSection {
  label: string;
  fields: { key: string; label: string }[];
}

export function ReportProblemModal({
  isOpen,
  onClose,
  applicationId,
  quoteResultId,
  vertical,
  insuredName,
  taskId,
  carrierDisplayName,
  coverageType,
  quoteData,
}: Props) {
  const token = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const [sections, setSections] = useState<EnabledSection[]>([]);
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set());
  const [fieldNotes, setFieldNotes] = useState<Record<string, string>>({});
  const [additionalComments, setAdditionalComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !token) return;
    setConfigLoading(true);
    setCheckedFields(new Set());
    setFieldNotes({});
    setAdditionalComments("");

    Promise.all([
      fetchFieldCatalog(token, vertical),
      fetchReportConfig(token, vertical),
    ])
      .then(([catalog, config]) => {
        const enabledKeys = new Set(config?.fields ?? []);

        // Filter catalog to only enabled fields, preserving section grouping
        const enabledSections: EnabledSection[] = [];
        for (const section of Object.values(catalog.sections)) {
          const enabledFields = Object.entries(section.fields)
            .filter(([key]) => enabledKeys.has(key))
            .map(([key, label]) => ({ key, label }));
          if (enabledFields.length > 0) {
            enabledSections.push({ label: section.label, fields: enabledFields });
          }
        }
        setSections(enabledSections);
      })
      .catch(() => {
        toast.error("Failed to load report configuration");
        setSections([]);
      })
      .finally(() => setConfigLoading(false));
  }, [isOpen, token, vertical]);

  const toggleField = (key: string) => {
    setCheckedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // Clear note when unchecking
        setFieldNotes((notes) => {
          const updated = { ...notes };
          delete updated[key];
          return updated;
        });
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const updateNote = (key: string, note: string) => {
    setFieldNotes((prev) => ({ ...prev, [key]: note }));
  };

  const handleSubmit = async () => {
    if (!token || !user) return;

    // The free-text detail is the only required input — field flags are now
    // auto-attached context, optional to the reporter (LIB-124).
    if (!additionalComments.trim()) {
      toast.error("Please describe the problem");
      return;
    }

    const flaggedFields: FlaggedField[] = Array.from(checkedFields).map(
      (key) => ({
        key,
        note: fieldNotes[key] || "",
      }),
    );

    // Auto-attach the known submission/quote context so the timeline event is
    // self-describing and FK-correlated. Omit empty keys so the producer's
    // null-tolerant metadata stays clean.
    const context: Record<string, unknown> = {};
    if (insuredName) context.insured_name = insuredName;
    if (taskId) context.task_id = taskId;
    if (carrierDisplayName) context.carrier_display_name = carrierDisplayName;
    if (coverageType) context.coverage_type = coverageType;
    if (quoteData) context.quote_data = quoteData;

    setLoading(true);
    try {
      await createProblemReport(token, applicationId, {
        vertical,
        reported_by: user.email,
        flagged_fields: flaggedFields,
        additional_comments: additionalComments,
        context: Object.keys(context).length > 0 ? context : undefined,
      });
      toast.success("Problem report submitted");
      onClose();
    } catch {
      toast.error("Failed to submit report");
    } finally {
      setLoading(false);
    }
  };

  const hasFields = sections.some((s) => s.fields.length > 0);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      variant="warning"
      title="Report a Problem"
      subtitle={
        quoteResultId
          ? "Report an issue with this quote"
          : "Report an issue with this submission"
      }
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={handleSubmit}
            disabled={loading || !additionalComments.trim()}
          >
            {loading ? "Submitting..." : "Submit Report"}
          </Button>
        </>
      }
    >
      {configLoading ? (
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : (
        <div className="space-y-5 max-h-[60vh] overflow-y-auto">
          {!hasFields && (
            <Typography as="p" variant="body" color="light">
              No specific fields are configured for this vertical — just describe
              the problem below.
            </Typography>
          )}
          {sections.map((section) => (
            <div key={section.label}>
              <Typography as="h4" variant="overline" color="light" className="mb-2">
                {section.label}
              </Typography>
              <div className="space-y-2">
                {section.fields.map((field) => (
                  <div key={field.key}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checkedFields.has(field.key)}
                        onChange={() => toggleField(field.key)}
                        className="w-4 h-4 rounded border-gray-300 text-navigator-primary focus:ring-navigator-primary/20 cursor-pointer"
                      />
                      <Typography as="span" variant="body" color="text-gray-700">{field.label}</Typography>
                    </label>
                    {checkedFields.has(field.key) && (
                      <div className="ml-7 mt-1">
                        <Input
                          inputSize="xs"
                          placeholder="What's wrong with this field?"
                          value={fieldNotes[field.key] || ""}
                          onChange={(e) => updateNote(field.key, e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Additional comments */}
          <div>
            <Typography as="h4" variant="overline" color="light" className="mb-2">
              Additional Comments
            </Typography>
            <textarea
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              placeholder="Any other issues not covered above..."
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-navigator-primary focus:ring-1 focus:ring-navigator-primary/20 focus:outline-none bg-white placeholder:text-gray-400 resize-y"
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}
